import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Scale, FileText, Mail } from "lucide-react";
import WhiteLogo from "../../assets/KavoaWhiteLogo.png";
import FooterSection from "../FooterSection";

const sections = [
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "description", label: "Description of Service" },
  { id: "registration", label: "Account Registration & Security" },
  { id: "subscription", label: "Subscription Plans & Payment" },
  { id: "acceptable-use", label: "Acceptable Use" },
  { id: "data-ownership", label: "Data Ownership & Intellectual Property" },
  { id: "confidentiality", label: "Confidentiality & Attorney-Client Privilege" },
  { id: "data-security", label: "Data Security" },
  { id: "availability", label: "Service Availability & Support" },
  { id: "liability", label: "Limitation of Liability" },
  { id: "indemnification", label: "Indemnification" },
  { id: "termination", label: "Termination" },
  { id: "changes", label: "Changes to Terms" },
  { id: "governing-law", label: "Governing Law" },
  { id: "contact", label: "Contact Information" },
];

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-80" />
        <div className="relative max-w-4xl mx-auto px-6 py-16">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-10"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </button>

          <div className="mb-8">
            <img
              src={WhiteLogo}
              alt="Kavoa"
              className="h-10 object-contain"
            />
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm">
              <Scale className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white tracking-tight">
              Terms of Service
            </h1>
          </div>

          <p className="text-slate-300 text-lg leading-relaxed max-w-2xl">
            Please read these Terms of Service carefully before using the Kavoa
            platform. These terms govern your access to and use of our law firm
            management software and related services.
          </p>

          <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
            <FileText className="h-4 w-4 text-brand-accent" />
            <span className="text-sm font-medium text-slate-200">
              Effective Date: January 1, 2026
            </span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Table of Contents */}
        <nav className="bg-slate-50 rounded-2xl p-8 mb-16">
          <h2 className="text-lg font-bold text-slate-900 mb-6">
            Table of Contents
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sections.map((section, index) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="flex items-center gap-3 text-sm text-slate-600 hover:text-brand transition-colors group"
              >
                <span
                  className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  {index + 1}
                </span>
                <span className="group-hover:underline">{section.label}</span>
              </a>
            ))}
          </div>
        </nav>

        {/* Sections */}
        <div className="space-y-16">
          {/* 1. Acceptance of Terms */}
          <section id="acceptance">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              1. Acceptance of Terms
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                By accessing or using the Kavoa platform ("Service"), including
                any associated websites, mobile applications, APIs, or related
                services provided by Kavoa, Inc. ("Kavoa," "we," "us," or
                "our"), you agree to be bound by these Terms of Service
                ("Terms"). If you are entering into these Terms on behalf of a
                law firm, legal practice, or other organization, you represent
                and warrant that you have the authority to bind that entity to
                these Terms, and the term "you" shall refer to such entity.
              </p>
              <p>
                If you do not agree to all of these Terms, you may not access or
                use the Service. Your continued use of the Service following any
                modifications to these Terms constitutes your acceptance of the
                revised Terms. We encourage you to review these Terms
                periodically to stay informed of any updates.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you
                and Kavoa. They apply to all users of the Service, including
                firm administrators, attorneys, paralegals, legal assistants,
                and any other personnel who access the platform under your
                account. You are responsible for ensuring that all users within
                your organization are aware of and comply with these Terms.
              </p>
            </div>
          </section>

          {/* 2. Description of Service */}
          <section id="description">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              2. Description of Service
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Kavoa is a cloud-based practice management platform designed
                specifically for law firms and legal professionals. The Service
                provides an integrated suite of tools to help legal practices
                operate more efficiently, reduce administrative burden, and
                deliver better outcomes for their clients. Kavoa is accessible
                through modern web browsers and is hosted on secure,
                enterprise-grade cloud infrastructure.
              </p>
              <p>
                The platform includes, but is not limited to, the following core
                features and modules:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Matter Management</strong>  -  Create, organize, and
                  track legal matters with customizable statuses, practice area
                  tagging, pipeline stages, and detailed matter notes. Associate
                  tasks, documents, billing entries, and communications with
                  individual matters for a complete case overview.
                </li>
                <li>
                  <strong>Client Management</strong>  -  Maintain comprehensive
                  client profiles including contact information, associated
                  matters, billing history, document repositories, and
                  communication logs. Manage both individual and organizational
                  clients with support for multiple contacts per entity.
                </li>
                <li>
                  <strong>Billing & Invoicing</strong>  -  Track billable and
                  non-billable time, generate professional invoices with
                  customizable templates, accept online payments, manage payment
                  plans, and monitor outstanding balances. Supports hourly, flat
                  fee, and contingency billing arrangements.
                </li>
                <li>
                  <strong>Document Management</strong>  -  Upload, organize, and
                  manage documents with folder structures, firm-wide templates,
                  and client-specific document repositories. Includes e-signature
                  capabilities for sending documents for client execution.
                </li>
                <li>
                  <strong>Calendar & Scheduling</strong>  -  Integrated calendar
                  with support for deadlines, court dates, appointments, and
                  reminders. Syncs with Google Calendar, Microsoft Outlook, and
                  Apple Calendar for seamless schedule management across
                  platforms.
                </li>
                <li>
                  <strong>Communications</strong>  -  Omnichannel messaging
                  including SMS, email, and voice calls directly within the
                  platform. All client communications are logged and associated
                  with the relevant matter for complete audit trails.
                </li>
                <li>
                  <strong>Trust Accounting</strong>  -  IOLTA-compliant trust
                  account management with client ledgers, receipt and
                  disbursement tracking, automatic reconciliation, payout
                  requests, and detailed audit trails to meet state bar
                  requirements.
                </li>
                <li>
                  <strong>Reporting & Analytics</strong>  -  Generate reports on
                  firm performance, matter overviews, outstanding balances, trust
                  balances, and other key metrics to support informed
                  decision-making.
                </li>
                <li>
                  <strong>Lead Intake</strong>  -  Capture, qualify, and convert
                  prospective client leads through a structured intake pipeline.
                  Track lead sources, manage follow-ups, and convert leads into
                  active clients and matters with guided workflows.
                </li>
                <li>
                  <strong>Immigration Case Tracking</strong>  -  Specialized tools
                  for immigration law practices including visa type tracking,
                  petition management, government filing deadlines, priority
                  dates, and status monitoring for all stages of immigration
                  proceedings.
                </li>
                <li>
                  <strong>Real Estate Transaction Management</strong>  - 
                  Purpose-built workflows for real estate attorneys including
                  transaction tracking, closing checklists, party management,
                  document coordination, and timeline management for purchase,
                  sale, and refinance transactions.
                </li>
              </ul>
              <p>
                Kavoa reserves the right to modify, update, or discontinue any
                feature or functionality of the Service at any time. We will
                provide reasonable notice of any material changes that
                significantly affect your use of the Service. New features or
                modules may be introduced under separate terms or included in
                specific subscription tiers.
              </p>
            </div>
          </section>

          {/* 3. Account Registration & Security */}
          <section id="registration">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              3. Account Registration & Security
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                To use the Service, you must register for an account by
                providing accurate, current, and complete information, including
                your name, email address, law firm name, and a secure password.
                You agree to update your registration information promptly to
                keep it accurate and complete at all times. Providing false or
                misleading information during registration may result in
                immediate termination of your account.
              </p>
              <p>
                You are solely responsible for maintaining the confidentiality
                of your account credentials, including your password and any
                authentication tokens. You must immediately notify Kavoa at{" "}
                <a
                  href="mailto:support@kavoa.io"
                  className="underline hover:text-brand transition-colors"
                  style={{ color: "var(--color-primary)" }}
                >
                  support@kavoa.io
                </a>{" "}
                if you become aware of any unauthorized access to or use of your
                account. Kavoa shall not be liable for any loss or damage
                arising from your failure to safeguard your account credentials.
              </p>
              <p>
                For firm accounts with multiple users, the individual who
                registers the account is designated as the Firm Administrator.
                The Firm Administrator is responsible for managing user access,
                assigning roles and permissions, inviting and removing team
                members, and ensuring that all users within the firm comply with
                these Terms. The Firm Administrator may delegate administrative
                responsibilities to other users but remains ultimately
                responsible for the account.
              </p>
              <p>
                Each user must have their own unique login credentials. Sharing
                of login credentials between individuals is strictly prohibited.
                You are responsible for all activities that occur under your
                account, whether or not you have authorized such activities.
                Kavoa supports role-based access controls, and it is the Firm
                Administrator's responsibility to configure appropriate
                permissions for each team member based on their role within the
                firm.
              </p>
            </div>
          </section>

          {/* 4. Subscription Plans & Payment */}
          <section id="subscription">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              4. Subscription Plans & Payment
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Kavoa offers a 14-day free trial that provides full access to
                the platform's features, allowing you to evaluate the Service
                before committing to a paid subscription. No credit card is
                required to begin your trial. At the conclusion of the trial
                period, you will need to select a paid subscription plan to
                continue accessing the Service and your data.
              </p>
              <p>
                The Service is available through the following subscription
                tiers, each designed to meet the needs of different practice
                sizes and specializations:
              </p>
              <div
                className="bg-slate-50 rounded-xl p-6 my-4 border-l-4"
                style={{ borderColor: "var(--color-primary)" }}
              >
                <ul className="space-y-3">
                  <li>
                    <strong>Starter (Basic)</strong>  -  Includes unlimited clients
                    and matters, calendar and task management, basic billing and
                    invoicing, document management, and reporting. Ideal for solo
                    practitioners and small firms getting started with practice
                    management software.
                  </li>
                  <li>
                    <strong>Professional (Pro)</strong>  -  Includes everything in
                    Starter, plus omnichannel messaging and calls (SMS, email,
                    voice), leads and intake pipeline, e-signatures, custom
                    pipeline stages, and payment plans with autopay. Designed for
                    growing firms that need advanced client communication and
                    intake tools.
                  </li>
                  <li>
                    <strong>Firm (Elite)</strong>  -  Includes everything in
                    Professional, plus immigration case management, real estate
                    transaction module, IOLTA-compliant trust accounting, custom
                    branding, and priority support. Built for specialized and
                    full-service firms requiring comprehensive practice
                    management capabilities.
                  </li>
                </ul>
              </div>
              <p>
                Subscriptions are billed on a per-user, per-month basis, with
                the option to pay monthly or annually. Annual billing provides a
                discounted rate. All subscriptions automatically renew at the end
                of each billing cycle unless cancelled prior to the renewal
                date. You authorize Kavoa to charge your designated payment
                method for all applicable fees at the beginning of each billing
                cycle.
              </p>
              <p>
                All payment processing is handled securely through Stripe, a
                PCI-DSS Level 1 certified payment processor. Kavoa does not
                directly store your credit card or bank account information.
                Payment details are transmitted securely to Stripe and managed
                in accordance with their privacy and security policies. You are
                responsible for keeping your payment information current.
              </p>
              <p>
                If you cancel your subscription, you will retain access to the
                Service through the end of your current billing period. After
                your subscription expires, your account will be placed in a
                read-only state, and your data will be retained for a period of
                90 days. During this retention period, you may export your data
                or reactivate your subscription. After 90 days, your data may
                be permanently deleted in accordance with our data retention
                policies. Kavoa is not responsible for data lost after the
                retention period has expired.
              </p>
            </div>
          </section>

          {/* 5. Acceptable Use */}
          <section id="acceptable-use">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              5. Acceptable Use
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                You agree to use the Service only for its intended purpose as a
                law firm management platform and in compliance with all
                applicable local, state, federal, and international laws and
                regulations. This includes, without limitation, laws governing
                the practice of law, legal ethics rules promulgated by your
                state bar or licensing authority, data privacy regulations, and
                anti-money laundering statutes.
              </p>
              <p>You expressly agree that you will not:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  Share your login credentials with any other person or allow
                  any unauthorized individual to access your account.
                </li>
                <li>
                  Use automated tools, scripts, bots, scrapers, or crawlers to
                  access, extract, or collect data from the Service without
                  Kavoa's prior written consent.
                </li>
                <li>
                  Attempt to reverse engineer, decompile, disassemble, or
                  otherwise attempt to discover the source code, algorithms, or
                  underlying architecture of the Service.
                </li>
                <li>
                  Circumvent, disable, or otherwise interfere with any security
                  features, access controls, or usage limitations of the
                  Service.
                </li>
                <li>
                  Upload, transmit, or store any content that is unlawful,
                  defamatory, fraudulent, or that infringes upon the
                  intellectual property rights of any third party.
                </li>
                <li>
                  Use the Service to transmit spam, unsolicited messages, or
                  malicious software, or to engage in any activity that
                  disrupts, degrades, or impairs the performance of the Service
                  for other users.
                </li>
                <li>
                  Sublicense, resell, rent, lease, or otherwise make the Service
                  available to any third party without Kavoa's prior written
                  authorization.
                </li>
                <li>
                  Use the Service in any manner that violates applicable rules
                  of professional conduct, including duties of competence,
                  confidentiality, and diligence owed to your clients.
                </li>
              </ul>
              <p>
                You acknowledge that you are solely responsible for your
                compliance with all applicable legal ethics rules and
                professional responsibility obligations. Kavoa provides tools to
                support your practice but does not monitor or enforce compliance
                with jurisdiction-specific rules of professional conduct. Kavoa
                reserves the right to suspend or terminate your account if we
                reasonably believe that your use of the Service violates these
                Terms or any applicable law.
              </p>
            </div>
          </section>

          {/* 6. Data Ownership & Intellectual Property */}
          <section id="data-ownership">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              6. Data Ownership & Intellectual Property
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                You retain full ownership of all data, content, documents,
                files, and information that you or your authorized users upload,
                create, or store within the Service ("User Data"). Kavoa does
                not claim any ownership rights over your User Data. As between
                you and Kavoa, all rights, title, and interest in and to your
                User Data remain exclusively with you.
              </p>
              <p>
                By using the Service, you grant Kavoa a limited, non-exclusive,
                worldwide, royalty-free license to access, process, transmit,
                store, and display your User Data solely to the extent necessary
                to provide, maintain, improve, and support the Service. This
                license is strictly limited to the operation of the Service and
                does not extend to any other use. Kavoa will not use your User
                Data for marketing, advertising, training machine learning
                models, or any purpose unrelated to providing the Service to
                you.
              </p>
              <p>
                Kavoa and its licensors retain all rights, title, and interest
                in and to the Service, including all software, technology,
                algorithms, user interfaces, designs, trademarks, service marks,
                logos, and all related intellectual property rights. Nothing in
                these Terms grants you any right to use Kavoa's trademarks,
                service marks, or branding without our prior written consent.
                The Service is protected by copyright, trade secret, and other
                intellectual property laws of the United States and
                international jurisdictions.
              </p>
              <p>
                Any feedback, suggestions, enhancement requests, or other
                recommendations that you provide to Kavoa regarding the Service
                ("Feedback") shall be the property of Kavoa. You hereby assign
                to Kavoa all rights in such Feedback, and Kavoa shall be free to
                use, incorporate, and commercialize such Feedback without
                restriction, obligation, or compensation to you.
              </p>
            </div>
          </section>

          {/* 7. Confidentiality & Attorney-Client Privilege */}
          <section id="confidentiality">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              7. Confidentiality & Attorney-Client Privilege
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Kavoa recognizes and respects the highly sensitive and
                confidential nature of the data processed through the Service.
                As a platform designed for law firms, we understand that User
                Data may include information protected by attorney-client
                privilege, attorney work product doctrine, and other legal
                protections. Kavoa is committed to maintaining the
                confidentiality of all User Data and will treat it with the
                highest level of care.
              </p>
              <div
                className="bg-brand-light rounded-xl p-6 my-4 border-l-4"
                style={{ borderColor: "var(--color-primary)" }}
              >
                <p className="text-slate-700 font-medium">
                  Kavoa will not access, review, or inspect your User Data
                  except as strictly necessary to provide, maintain, and support
                  the Service, to respond to your specific support requests, to
                  comply with applicable law or valid legal process, or to
                  enforce these Terms. Access to User Data by Kavoa personnel is
                  limited to authorized employees and contractors who have a
                  legitimate need and who are bound by confidentiality
                  obligations.
                </p>
              </div>
              <p>
                Kavoa will never sell, license, share, or otherwise disclose
                your User Data to third parties for their marketing, advertising,
                or commercial purposes. We do not monetize your data in any way.
                The only third parties that may have access to your data are our
                infrastructure and service providers (such as cloud hosting and
                payment processing providers), and such access is limited to
                what is necessary for them to perform their services on our
                behalf, subject to confidentiality obligations.
              </p>
              <p>
                While Kavoa takes all reasonable measures to protect the
                confidentiality of your data, you acknowledge that the use of
                any cloud-based service involves inherent risks. You are
                responsible for evaluating whether the Service meets the
                confidentiality and security requirements applicable to your
                practice, including any obligations imposed by your state bar,
                licensing authority, or applicable data protection regulations.
                Kavoa recommends that you consult your professional
                responsibility obligations and, where appropriate, obtain
                informed consent from your clients before storing their
                information on any cloud-based platform.
              </p>
            </div>
          </section>

          {/* 8. Data Security */}
          <section id="data-security">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              8. Data Security
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Kavoa implements and maintains comprehensive administrative,
                technical, and physical security measures designed to protect
                your User Data against unauthorized access, disclosure,
                alteration, destruction, or loss. Our security practices are
                designed to meet or exceed industry standards for SaaS
                applications processing sensitive professional data.
              </p>
              <p>Our security measures include, but are not limited to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Encryption at Rest</strong>  -  All User Data stored
                  within the Service is encrypted using AES-256 encryption or
                  equivalent industry-standard encryption algorithms.
                </li>
                <li>
                  <strong>Encryption in Transit</strong>  -  All data transmitted
                  between your device and our servers is encrypted using TLS 1.2
                  or higher, ensuring that your information is protected during
                  transmission.
                </li>
                <li>
                  <strong>Regular Backups</strong>  -  User Data is backed up on a
                  regular schedule with geographically redundant storage to
                  protect against data loss. Backup data is encrypted and
                  retained in accordance with our data retention policies.
                </li>
                <li>
                  <strong>Access Controls</strong>  -  Our infrastructure employs
                  strict access controls, multi-factor authentication for
                  administrative access, and role-based permissions to limit
                  data access to authorized personnel only.
                </li>
                <li>
                  <strong>Security Monitoring</strong>  -  We maintain continuous
                  monitoring of our systems for suspicious activity, potential
                  vulnerabilities, and security incidents. Our infrastructure
                  undergoes regular security assessments and penetration testing.
                </li>
                <li>
                  <strong>SOC 2 Alignment</strong>  -  Kavoa's security controls
                  are designed in alignment with SOC 2 Type II standards for
                  security, availability, and confidentiality. We maintain
                  documented security policies and procedures and conduct
                  regular internal audits.
                </li>
              </ul>
              <div
                className="bg-slate-50 rounded-xl p-6 my-4 border-l-4"
                style={{ borderColor: "var(--color-primary)" }}
              >
                <p className="text-slate-700 font-medium">
                  In the event of a confirmed data breach that affects your User
                  Data, Kavoa will notify you within 72 hours of becoming aware
                  of the breach. Notification will include a description of the
                  nature of the breach, the types of data potentially affected,
                  the measures we are taking to address the breach, and
                  recommendations for steps you may take to protect yourself
                  and your clients.
                </p>
              </div>
              <p>
                While Kavoa employs robust security measures, no system is
                completely immune to all security threats. You acknowledge that
                you are responsible for implementing appropriate security
                practices within your organization, including using strong and
                unique passwords, enabling any available multi-factor
                authentication, keeping your devices and software up to date,
                and training your staff on security awareness.
              </p>
            </div>
          </section>

          {/* 9. Service Availability & Support */}
          <section id="availability">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              9. Service Availability & Support
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Kavoa strives to maintain a 99.9% uptime target for the Service,
                measured on a monthly basis. This target reflects our commitment
                to providing reliable and consistent access to the platform.
                However, this uptime target is a goal and not a guarantee, and
                Kavoa shall not be liable for any downtime or service
                interruptions that occur.
              </p>
              <p>
                Scheduled maintenance windows may be required from time to time
                to perform system updates, security patches, infrastructure
                upgrades, and other maintenance activities. Kavoa will use
                commercially reasonable efforts to schedule maintenance during
                off-peak hours (typically between 12:00 AM and 6:00 AM Eastern
                Time on weekends) and to provide advance notice of any planned
                downtime via email or in-app notification. Emergency maintenance
                required to address critical security vulnerabilities or system
                stability issues may be performed without advance notice.
              </p>
              <p>
                The uptime target excludes downtime resulting from circumstances
                beyond Kavoa's reasonable control, including but not limited to:
                force majeure events, failures of third-party infrastructure or
                internet service providers, distributed denial-of-service
                attacks, your own hardware or software failures, or scheduled
                maintenance windows.
              </p>
              <p>
                Customer support is available via email at{" "}
                <a
                  href="mailto:support@kavoa.io"
                  className="underline hover:text-brand transition-colors"
                  style={{ color: "var(--color-primary)" }}
                >
                  support@kavoa.io
                </a>
                . We aim to respond to all support inquiries within one business
                day. Subscribers on the Firm (Elite) plan receive priority
                support with expedited response times. Kavoa may also provide
                support through in-app help resources, documentation, and
                knowledge base articles.
              </p>
            </div>
          </section>

          {/* 10. Limitation of Liability */}
          <section id="liability">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              10. Limitation of Liability
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
                SHALL KAVOA, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS,
                AFFILIATES, SUCCESSORS, OR ASSIGNS BE LIABLE FOR ANY INDIRECT,
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY
                DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF
                PROFITS, REVENUE, GOODWILL, DATA, USE, OR OTHER INTANGIBLE
                LOSSES, ARISING OUT OF OR IN CONNECTION WITH YOUR ACCESS TO OR
                USE OF (OR INABILITY TO ACCESS OR USE) THE SERVICE, REGARDLESS
                OF THE THEORY OF LIABILITY (CONTRACT, TORT, STRICT LIABILITY,
                OR OTHERWISE), EVEN IF KAVOA HAS BEEN ADVISED OF THE POSSIBILITY
                OF SUCH DAMAGES.
              </p>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, KAVOA'S
                TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF
                OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE
                GREATER OF (A) THE TOTAL FEES PAID BY YOU TO KAVOA DURING THE
                TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE
                TO THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
              </p>
              <div
                className="bg-amber-50 rounded-xl p-6 my-4 border-l-4 border-amber-400"
              >
                <p className="text-slate-700 font-medium">
                  Kavoa is a practice management tool and does not provide legal
                  advice, legal opinions, or legal representation of any kind.
                  The Service is not a substitute for the professional judgment
                  of a licensed attorney. Kavoa shall not be liable for any
                  outcomes, consequences, or damages arising from legal advice
                  given by you to your clients, legal strategies you pursue,
                  court filings you make, or any other actions taken by you in
                  the course of your legal practice, regardless of whether such
                  actions were facilitated by or related to the use of the
                  Service.
                </p>
              </div>
              <p>
                You acknowledge that the fees charged for the Service reflect
                the allocation of risk set forth in this limitation of liability
                and that Kavoa would not enter into these Terms without these
                limitations. Some jurisdictions do not allow the exclusion or
                limitation of certain damages, so some of the above limitations
                may not apply to you. In such cases, Kavoa's liability shall be
                limited to the fullest extent permitted by applicable law.
              </p>
            </div>
          </section>

          {/* 11. Indemnification */}
          <section id="indemnification">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              11. Indemnification
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                You agree to indemnify, defend, and hold harmless Kavoa and its
                officers, directors, employees, agents, affiliates, successors,
                and assigns from and against any and all claims, demands,
                actions, liabilities, damages, losses, costs, and expenses
                (including reasonable attorneys' fees and court costs) arising
                out of or in connection with: (a) your use of or access to the
                Service; (b) any breach of these Terms by you or your authorized
                users; (c) any violation of applicable law, regulation, or rule
                of professional conduct by you or your authorized users; or (d)
                any claim by a third party (including your clients) related to
                your provision of legal services, your use of the Service, or
                the content of any User Data you store or process through the
                Service.
              </p>
              <p>
                Kavoa reserves the right, at its own expense, to assume the
                exclusive defense and control of any matter otherwise subject to
                indemnification by you, in which event you will cooperate fully
                with Kavoa in asserting any available defenses. You agree not to
                settle any matter subject to this indemnification without the
                prior written consent of Kavoa.
              </p>
              <p>
                This indemnification obligation shall survive the termination or
                expiration of these Terms and your use of the Service. You
                acknowledge that your obligation to indemnify Kavoa is a
                material condition of these Terms and that Kavoa has relied upon
                this obligation in making the Service available to you.
              </p>
            </div>
          </section>

          {/* 12. Termination */}
          <section id="termination">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              12. Termination
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Either party may terminate these Terms at any time. You may
                terminate your account by cancelling your subscription through
                the account settings page or by contacting Kavoa at{" "}
                <a
                  href="mailto:support@kavoa.io"
                  className="underline hover:text-brand transition-colors"
                  style={{ color: "var(--color-primary)" }}
                >
                  support@kavoa.io
                </a>
                . Upon cancellation by you, your access to the Service will
                continue through the end of your current billing period.
              </p>
              <p>
                Kavoa may suspend or terminate your access to the Service
                immediately, without prior notice or liability, if: (a) you
                breach any provision of these Terms; (b) you fail to pay any
                fees when due; (c) your use of the Service poses a security risk
                to the platform or other users; (d) your use of the Service may
                expose Kavoa to legal liability; or (e) Kavoa is required to do
                so by law or valid legal process. In the case of a non-material
                breach, Kavoa will use reasonable efforts to provide notice and
                an opportunity to cure before terminating your account.
              </p>
              <p>
                Upon termination of your account for any reason, you will have a
                30-day data export period during which you may request an export
                of your User Data in a standard, machine-readable format. After
                this export period, Kavoa will retain your data for an
                additional 60 days (for a total of 90 days post-termination)
                before permanent deletion. You may request immediate deletion of
                your data at any time by contacting support. Once data is
                permanently deleted, it cannot be recovered.
              </p>
              <p>
                The following provisions of these Terms shall survive
                termination: Data Ownership & Intellectual Property,
                Confidentiality, Limitation of Liability, Indemnification,
                Governing Law, and any other provisions that by their nature
                should survive termination.
              </p>
            </div>
          </section>

          {/* 13. Changes to Terms */}
          <section id="changes">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              13. Changes to Terms
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Kavoa reserves the right to modify, amend, or update these Terms
                at any time at our sole discretion. For material changes that
                significantly affect your rights or obligations under these
                Terms, we will provide at least 30 days' advance notice before
                the changes take effect. Notice may be provided via email to
                the address associated with your account, through an in-app
                notification, or by posting a prominent notice on the Kavoa
                website.
              </p>
              <p>
                Non-material changes, such as corrections of typographical
                errors, updates to formatting, or clarifications that do not
                alter the substance of these Terms, may be made at any time
                without advance notice. The "Effective Date" at the top of these
                Terms will be updated to reflect the date of the most recent
                revision.
              </p>
              <p>
                Your continued use of the Service after the effective date of
                any changes to these Terms constitutes your acceptance of the
                revised Terms. If you do not agree to the revised Terms, you
                must discontinue your use of the Service before the changes take
                effect. You may terminate your account as described in Section
                12 if you do not agree to any modifications. It is your
                responsibility to review these Terms periodically to stay
                informed of updates.
              </p>
            </div>
          </section>

          {/* 14. Governing Law */}
          <section id="governing-law">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              14. Governing Law
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                These Terms and any dispute arising out of or relating to these
                Terms or the Service shall be governed by and construed in
                accordance with the laws of the State of Delaware, United States
                of America, without regard to its conflict of laws principles.
                You agree that any legal action or proceeding arising out of or
                relating to these Terms shall be brought exclusively in the
                federal or state courts located in the State of Delaware, and
                you consent to the personal jurisdiction of such courts.
              </p>
              <p>
                Any claim or cause of action arising out of or relating to these
                Terms or the Service must be filed within one (1) year after the
                date on which such claim or cause of action arose, or it shall
                be forever barred. This limitation period applies to all claims,
                whether based in contract, tort, statute, or any other legal
                theory.
              </p>
              <p>
                If any provision of these Terms is found by a court of competent
                jurisdiction to be invalid, illegal, or unenforceable, the
                remaining provisions of these Terms shall continue in full force
                and effect. The invalid or unenforceable provision shall be
                modified to the minimum extent necessary to make it valid and
                enforceable while preserving its original intent, or if
                modification is not possible, it shall be severed from these
                Terms. The failure of Kavoa to enforce any right or provision of
                these Terms shall not constitute a waiver of that right or
                provision.
              </p>
            </div>
          </section>

          {/* 15. Contact Information */}
          <section id="contact">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              15. Contact Information
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                If you have any questions, concerns, or requests regarding these
                Terms of Service, your account, or the Kavoa platform, please
                contact us. We are committed to addressing your inquiries
                promptly and thoroughly.
              </p>
              <div
                className="bg-slate-50 rounded-xl p-6 border-l-4"
                style={{ borderColor: "var(--color-primary)" }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="p-2.5 rounded-xl flex-shrink-0"
                    style={{ backgroundColor: "#E8F5E9" }}
                  >
                    <Mail
                      className="h-5 w-5"
                      style={{ color: "var(--color-primary)" }}
                    />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 mb-1">
                      Kavoa, Inc.
                    </p>
                    <p className="text-slate-600">
                      Email:{" "}
                      <a
                        href="mailto:support@kavoa.io"
                        className="underline hover:text-brand transition-colors"
                        style={{ color: "var(--color-primary)" }}
                      >
                        support@kavoa.io
                      </a>
                    </p>
                    <p className="text-slate-500 text-sm mt-2">
                      We aim to respond to all inquiries within one business day.
                    </p>
                  </div>
                </div>
              </div>
              <p>
                For urgent matters related to account security or data breaches,
                please include "URGENT" in the subject line of your email to
                ensure expedited handling. For legal notices required to be
                delivered under these Terms, please send correspondence to the
                email address above with the subject line "Legal Notice."
              </p>
            </div>
          </section>
        </div>
      </main>

      <FooterSection />
    </div>
  );
};

export default TermsOfService;
